import { useRef, useMemo } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { ChecklistField, Text, useControlledField } from '@frontend/design-system';
import { AxisGroup } from './types';

export const CheckboxGroup = ({
  group,
  draftY,
  onCheckboxChange,
  disabledOptions = [],
}: {
  group: AxisGroup;
  draftY: string[];
  onCheckboxChange: (values: string[]) => void;
  disabledOptions?: string[];
}) => {
  // Use refs to always access the latest values inside the onChange callback,
  // avoiding stale closure issues with useControlledField's internal handler caching.
  const draftYRef = useRef(draftY);
  draftYRef.current = draftY;

  const groupValueKey = group.options.map((o) => o.value).join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- groupValueKey is a stable string proxy for group.options
  const groupValues = useMemo(() => group.options.map((o) => o.value), [groupValueKey]);

  const groupValuesRef = useRef(groupValues);
  groupValuesRef.current = groupValues;

  const selectedInGroup = useMemo(() => draftY.filter((v) => groupValues.includes(v)), [draftY, groupValues]);

  const checklistProps = useControlledField({
    type: 'checklist',
    value: selectedInGroup,
    onChange: (newGroupSelection) => {
      // Read from refs to guarantee we always merge against the latest state
      const currentDraftY = draftYRef.current;
      const currentGroupValues = groupValuesRef.current;
      const otherGroupSelections = currentDraftY.filter((v) => !currentGroupValues.includes(v));
      onCheckboxChange([...otherGroupSelections, ...newGroupSelection]);
    },
  });

  return (
    <ChecklistField
      {...checklistProps}
      name={group.label}
      label={group.label}
      showLabel={false}
      css={styles.checklistField}
    >
      {group.options.map((option) => (
        <ChecklistField.Option
          key={option.value}
          name={option.value}
          css={{ display: 'flex', alignItems: 'center' }}
          disabled={disabledOptions.includes(option.value)}
        >
          <Text size='large'>{option.label}</Text>
        </ChecklistField.Option>
      ))}
    </ChecklistField>
    // </div>
  );
};

const styles = {
  checklistField: css({
    '> div': {
      marginBottom: theme.spacing(1),
    },
    '> div:last-child': {
      marginBottom: 0,
    },
  }),
};
